import {lazyLoadBackground} from './lazy-loading';

/**
 * Image Carousel Module
 * 
 * Implements a responsive image carousel with lazy loading and smooth transitions.
 * Uses IntersectionObserver for performance and manages its own animation state.
 * 
 * Features:
 * - Lazy loads images only when needed
 * - Smooth transitions between slides
 * - Automatic rotation with configurable timing
 * - Pauses when out of viewport
 * - Progress indicator for current slide
 * - Handles z-index stacking for smooth transitions
 * 
 * Implementation Details:
 * - Uses data-index attributes for slide tracking
 * - Manages loading, visibility, and transition states separately
 * - Pre-loads next image for smooth transitions
 * - Cleans up timers and observers when not in view
 * 
 * Required HTML Structure:
 * <div class="carousel">
 *   <div class="carousel__image" data-index="0" data-bg-url="...">
 *   <div class="carousel__progress__tracker">
 * 
 * @requires lazy-loading.js for image loading functionality
 */
export const initCarousel = () => {
    const VISIBLE_DURATION = 6000;
    const TRANSITION_DURATION = 500;

    let carouselTimeout; // For tracking our setTimeout
    let hasStarted = false;  // Track if we've started rotating yet
    let isInitialized = false;  // Track if carousel has been initialized

    const rotateCarousel = async () => {
        if (!isInitialized) return;
        const images = document.querySelectorAll('.carousel__image');
        const currentActive = document.querySelector('.carousel__image.active');
        const progressTracker = document.querySelector('.carousel__progress__tracker');
        
        const currentIndex = currentActive ? parseInt(currentActive.dataset.index) : -1;
        const nextIndex = currentIndex + 1 >= images.length ? 0 : currentIndex + 1;
        
        // Reset and restart progress animation
        progressTracker.classList.remove('animating');
        progressTracker.offsetHeight; // Triggers a re-draw
        progressTracker.classList.add('animating');
        
        const nextElement = images[nextIndex];
        const originalZIndex = images.length - parseInt(nextElement.dataset.index);

        // console.log('rotating now');
        
        if (hasStarted) {  // Only do transitions after first timeout
            // Load current and next images
            await loadImage(currentActive || nextElement);
            preloadNextImage(currentIndex, images);  // Don't wait, it's pre-loading

            if (nextIndex === 0) {
                nextElement.style.zIndex = '-1';
                
                nextElement.classList.add('active');
                currentActive.classList.add('removing');
                
                setTimeout(() => {
                    currentActive.classList.remove('active', 'removing');
                    nextElement.style.zIndex = originalZIndex;
                }, TRANSITION_DURATION);
            } else {
                nextElement.classList.add('active');
                // showBackground(nextElement);  // Show the background when it becomes active
                currentActive.classList.add('removing');
                
                setTimeout(() => {
                    currentActive.classList.remove('active', 'removing');
                }, TRANSITION_DURATION);
            }
        } else {
            hasStarted = true;  // Mark that we've started rotating
        }
        
        carouselTimeout = setTimeout(rotateCarousel, VISIBLE_DURATION);
    }

    const initializeCarousel = async () => {
        if (isInitialized) return;
        
        const images = document.querySelectorAll('.carousel__image');
        const currentActive = document.querySelector('.carousel__image.active');
        const nextIndex = currentActive ? 
            (parseInt(currentActive.dataset.index) + 1 >= images.length ? 0 : parseInt(currentActive.dataset.index) + 1) 
            : 0;
        
        // Load first two images before starting
        await loadImage(currentActive || images[0]);
        await loadImage(images[nextIndex]);
        
        // console.log('carousel initialized now');
        isInitialized = true;
        rotateCarousel();
    };

    const handleIntersect = async (entries) => {
        entries.forEach(async (entry) => {
            if (entry.isIntersecting) {
                if (!carouselTimeout && !isInitialized) {
                    // console.log('initializing carousel');
                    await initializeCarousel();
                } else if (!carouselTimeout && isInitialized) {
                    // console.log('rotating carousel');
                    rotateCarousel();
                }
            } else {
                // console.log('stopping animations now');
                // Stop carousel when out of view
                clearTimeout(carouselTimeout);
                carouselTimeout = null;

                // Restart the timeout state
                hasStarted = false;

                // Also stop progress bar animation
                const progressTracker = document.querySelector('.carousel__progress__tracker');
                progressTracker.classList.remove('animating');
            }
        });
    };

    const loadImage = async (element) => {
        await lazyLoadBackground(element, {
            fadeDuration: '0.5s',
        });
    };

    const preloadNextImage = (currentIndex, images) => {
        const nextIndex = currentIndex + 1 >= images.length ? 0 : currentIndex + 1;
        loadImage(images[nextIndex]);
    };

    // Only initialize if carousel exists
    if (document.querySelector('.carousel')) {
        document.addEventListener('DOMContentLoaded', () => {
            const carouselContainer = document.querySelector('.carousel__image').parentElement;
            const observer = new IntersectionObserver(handleIntersect, {
                root: null,
                threshold: 0.3 // Trigger when 30% of carousel is visible
            });
            
            observer.observe(carouselContainer);
        });
    }
};
